/**
 * TeamOnboardingChecklist
 *
 * A checklist component shown on TeamSettingsPage for new team owners.
 * Guides users through initial team setup tasks.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, UserPlus, CreditCard, GitBranch, X, ChevronRight } from 'lucide-react';
import { Tenant } from '../contexts/TenantContext';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  action?: () => void;
  icon: React.ReactNode;
}

interface TeamOnboardingChecklistProps {
  tenant: Tenant;
  memberCount?: number;
  hasConnectedRepo?: boolean;
  hasSetupBilling?: boolean;
}

const CHECKLIST_DISMISSED_KEY = 'team_onboarding_checklist_dismissed';

export const TeamOnboardingChecklist: React.FC<TeamOnboardingChecklistProps> = ({
  tenant,
  memberCount = 1,
  hasConnectedRepo = false,
  hasSetupBilling = false,
}) => {
  const navigate = useNavigate();
  const [isDismissed, setIsDismissed] = useState(false);

  // Check if checklist was previously dismissed for this tenant
  useEffect(() => {
    const dismissedTenants = localStorage.getItem(CHECKLIST_DISMISSED_KEY);
    if (dismissedTenants) {
      try {
        const dismissed = JSON.parse(dismissedTenants);
        if (Array.isArray(dismissed) && dismissed.includes(tenant.id)) {
          setIsDismissed(true);
        }
      } catch {
        // Invalid JSON, ignore
      }
    }
  }, [tenant.id]);

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    const dismissedTenants = localStorage.getItem(CHECKLIST_DISMISSED_KEY);
    let dismissed: string[] = [];
    if (dismissedTenants) {
      try {
        dismissed = JSON.parse(dismissedTenants);
        if (!Array.isArray(dismissed)) dismissed = [];
      } catch {
        dismissed = [];
      }
    }
    if (!dismissed.includes(tenant.id)) {
      dismissed.push(tenant.id);
    }
    localStorage.setItem(CHECKLIST_DISMISSED_KEY, JSON.stringify(dismissed));
    setIsDismissed(true);
  }, [tenant.id]);

  // Create checklist items
  const checklistItems: ChecklistItem[] = [
    {
      id: 'team_created',
      title: 'Create your team',
      description: 'Your team is set up and ready',
      completed: true, // Always true since they have a team
      icon: <Check className="w-5 h-5" />,
    },
    {
      id: 'invite_members',
      title: 'Invite team members',
      description: 'Add colleagues to collaborate',
      completed: memberCount > 1,
      action: () => {
        // Scroll to invitations section or open invite modal
        const inviteSection = document.getElementById('invite-members-section');
        if (inviteSection) {
          inviteSection.scrollIntoView({ behavior: 'smooth' });
        }
      },
      icon: <UserPlus className="w-5 h-5" />,
    },
    {
      id: 'setup_billing',
      title: 'Set up billing',
      description: 'Choose a plan for your team',
      completed: hasSetupBilling,
      action: () => navigate('/settings/billing'),
      icon: <CreditCard className="w-5 h-5" />,
    },
    {
      id: 'connect_repo',
      title: 'Connect a repository',
      description: 'Start analyzing your code',
      completed: hasConnectedRepo,
      action: () => navigate('/repositories'),
      icon: <GitBranch className="w-5 h-5" />,
    },
  ];

  // Calculate progress
  const completedCount = checklistItems.filter((item) => item.completed).length;
  const totalCount = checklistItems.length;
  const allCompleted = completedCount === totalCount;

  // Don't show if dismissed or all completed
  if (isDismissed || allCompleted) {
    return null;
  }

  return (
    <div
      className="rounded-xl border mb-6 overflow-hidden"
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderColor: 'var(--color-border-default)',
      }}
    >
      {/* Header */}
      <div
        className="px-6 py-4 flex items-center justify-between"
        style={{
          borderBottom: '1px solid var(--color-border-default)',
          backgroundColor: 'var(--color-bg-tertiary)',
        }}
      >
        <div>
          <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Get Started with {tenant.name}
          </h3>
          <p className="text-sm mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
            {completedCount} of {totalCount} tasks completed
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="p-2 rounded-lg hover:bg-(--color-bg-secondary) transition-colors"
          style={{ color: 'var(--color-text-tertiary)' }}
          title="Dismiss checklist"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="h-1" style={{ backgroundColor: 'var(--color-border-default)' }}>
        <div
          className="h-full transition-all duration-300"
          style={{
            backgroundColor: 'var(--color-accent-teal)',
            width: `${(completedCount / totalCount) * 100}%`,
          }}
        />
      </div>

      {/* Checklist Items */}
      <div className="divide-y" style={{ borderColor: 'var(--color-border-default)' }}>
        {checklistItems.map((item) => (
          <div
            key={item.id}
            className={`px-6 py-4 flex items-center gap-4 ${
              !item.completed && item.action ? 'cursor-pointer hover:bg-tertiary' : ''
            } transition-colors`}
            onClick={!item.completed && item.action ? item.action : undefined}
          >
            {/* Status Icon */}
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                item.completed ? '' : 'border-2'
              }`}
              style={{
                backgroundColor: item.completed ? 'var(--color-accent-green)' : 'transparent',
                borderColor: item.completed ? undefined : 'var(--color-border-default)',
                color: item.completed ? 'white' : 'var(--color-text-tertiary)',
              }}
            >
              {item.completed ? <Check className="w-5 h-5" /> : item.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p
                className={`font-medium ${item.completed ? 'line-through' : ''}`}
                style={{
                  color: item.completed
                    ? 'var(--color-text-tertiary)'
                    : 'var(--color-text-primary)',
                }}
              >
                {item.title}
              </p>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                {item.description}
              </p>
            </div>

            {/* Action Arrow */}
            {!item.completed && item.action && (
              <ChevronRight
                className="w-5 h-5 shrink-0"
                style={{ color: 'var(--color-text-tertiary)' }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Dismiss Link */}
      <div
        className="px-6 py-3 text-center"
        style={{
          borderTop: '1px solid var(--color-border-default)',
          backgroundColor: 'var(--color-bg-tertiary)',
        }}
      >
        <button
          onClick={handleDismiss}
          className="text-sm hover:underline"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Dismiss checklist
        </button>
      </div>
    </div>
  );
};

export default TeamOnboardingChecklist;
